/* After Tempo — product page behaviour: options, gallery, size guide, sticky add-to-cart.
   Add-to-cart itself is handled by Dawn's <product-form>. */
(() => {
  class AtProduct {
    constructor(root) {
      this.root = root;
      this.variants = JSON.parse(root.querySelector('[data-at-variants]').textContent);
      this.fieldsets = [...root.querySelectorAll('[data-at-option]')];
      this.colourSet = root.querySelector('.at-opt--colour');
      this.sizeSet = root.querySelector('[data-at-size]');
      this.input = root.querySelector('[data-at-variant-input]');
      this.atc = root.querySelector('[data-at-atc]');
      this.atcText = this.atc?.querySelector('span');
      this.dynamic = root.querySelector('[data-at-dynamic]');
      this.priceNow = root.querySelector('[data-at-price-now]');
      this.priceWas = root.querySelector('[data-at-price-was]');
      this.priceTag = root.querySelector('[data-at-price-tag]');
      this.priceGuarantee = root.querySelector('[data-at-price-guarantee]');
      this.pairs = root.querySelector('[data-at-pairs]');
      this.quantity = root.querySelector('[data-at-quantity]');
      this.pairsOne = root.querySelector('[data-at-pairs-one]');
      this.pairsTwo = root.querySelector('[data-at-pairs-two]');
      this.track = root.querySelector('[data-at-track]');
      this.slides = [...root.querySelectorAll('.at-gallery__slide')];
      this.thumbs = [...root.querySelectorAll('[data-at-thumb]')];
      this.sticky = root.querySelector('[data-at-sticky]');
      this.stickyBtn = root.querySelector('[data-at-sticky-btn]');
      this.stickyPrice = root.querySelector('[data-at-sticky-price]');

      this.bindOptions();
      this.bindPairs();
      this.bindGallery();
      this.bindDialogs();
      this.bindSticky();
      this.update();
    }

    selected() {
      return this.fieldsets.map((set) => set.querySelector('input:checked')?.value ?? null);
    }

    findVariant(selection) {
      if (selection.some((value) => value === null)) return null;
      return this.variants.find((v) => v.options.every((option, i) => option === selection[i])) || null;
    }

    bindOptions() {
      this.fieldsets.forEach((set) => {
        set.addEventListener('change', () => {
          const value = set.querySelector('input:checked')?.value ?? '';
          const label = set.querySelector('[data-at-option-value]');
          if (label) label.textContent = value;
          if (set === this.colourSet) this.showColour(value);
          this.update();
        });
      });
    }

    // One pair or two: sets the quantity that goes to the cart.
    bindPairs() {
      if (!this.pairs) return;
      this.pairs.addEventListener('change', () => this.update());
    }

    pairCount() {
      return Number(this.pairs?.querySelector('input:checked')?.value) || 1;
    }

    showColour(value) {
      const index = Number(this.colourSet.dataset.atOption);
      const match = this.variants.find((v) => v.options[index] === value && v.media);
      if (match) this.goToMedia(match.media);
    }

    update() {
      const selection = this.selected();
      const variant = this.findVariant(selection);
      const missing = this.fieldsets.find((set) => !set.querySelector('input:checked'));

      // Price follows the chosen variant, or the first variant in the chosen colour.
      const priceSource =
        variant ||
        this.variants.find((v) => selection.every((value, i) => value === null || v.options[i] === value)) ||
        this.variants[0];
      const pairs = this.pairCount();
      if (this.quantity) this.quantity.value = pairs;
      if (this.pairsOne) this.pairsOne.textContent = priceSource.price;
      if (this.pairsTwo) this.pairsTwo.textContent = priceSource.two;
      if (this.priceNow) this.priceNow.textContent = priceSource.price;
      if (this.stickyPrice) this.stickyPrice.textContent = pairs === 2 ? priceSource.two : priceSource.price;
      if (this.priceGuarantee) this.priceGuarantee.textContent = priceSource.price;
      if (this.priceWas) {
        this.priceWas.hidden = !priceSource.compare;
        if (priceSource.compare) this.priceWas.textContent = priceSource.compare;
      }
      if (this.priceTag) this.priceTag.hidden = !priceSource.compare;

      if (!this.atc) return;
      let label;
      let enabled = false;
      if (variant && variant.available) {
        label = (pairs === 2 && this.atc.dataset.labelAddTwo) || this.atc.dataset.labelAdd;
        enabled = true;
      } else if (variant) {
        label = this.atc.dataset.labelSold;
      } else {
        const name = missing?.dataset.name?.toLowerCase() || 'size';
        label = `Select a ${name}`;
      }

      this.input.value = variant ? variant.id : '';
      this.atcText.textContent = label;
      this.atc.toggleAttribute('disabled', !enabled);
      if (enabled) this.atc.removeAttribute('aria-disabled');
      else this.atc.setAttribute('aria-disabled', 'true');
      if (this.dynamic) this.dynamic.hidden = !enabled;
      if (this.stickyBtn) this.stickyBtn.textContent = enabled ? label : 'Select a size';

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    bindGallery() {
      if (!this.track) return;
      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
            this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
            ticking = false;
          });
        },
        { passive: true }
      );
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goToIndex(i)));
    }

    goToIndex(index) {
      if (!this.track || index < 0) return;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
    }

    goToMedia(mediaId) {
      this.goToIndex(this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId)));
    }

    bindDialogs() {
      this.root.querySelectorAll('[data-at-open]').forEach((opener) => {
        const dialog = document.getElementById(opener.dataset.atOpen);
        if (!dialog || typeof dialog.showModal !== 'function') return;
        opener.addEventListener('click', () => dialog.showModal());
        dialog.querySelector('[data-at-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
      });
    }

    bindSticky() {
      if (!this.sticky || !this.atc) return;
      const observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', passed);
        this.sticky.setAttribute('aria-hidden', String(!passed));
        this.stickyBtn.tabIndex = passed ? 0 : -1;
      });
      observer.observe(this.atc);

      this.stickyBtn.addEventListener('click', () => {
        if (!this.atc.disabled) {
          this.atc.click();
          return;
        }
        const target = this.sizeSet || this.fieldsets.find((set) => !set.querySelector('input:checked'));
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.querySelector('input')?.focus({ preventScroll: true });
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-at-product]').forEach((root) => {
      if (root.dataset.atReady) return;
      root.dataset.atReady = 'true';
      new AtProduct(root);
    });
  };

  // Colour links elsewhere on the page (the at-a-glance section) choose that colour in the buy box.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-at-pick-colour]');
    if (!link) return;
    const root = document.querySelector('[data-at-product]');
    const inputs = [...(root?.querySelectorAll('.at-opt--colour input') || [])];
    const input = inputs.find((el) => el.value === link.dataset.atPickColour);
    if (!input) return;
    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
